// CRM SDK validation helpers.
//
// Kept in a separate module so crm.ts stays focused on CRUD wiring. Every
// helper here is pure — no Firebase calls, no module-level state. The CRM
// SDK calls these at the boundary so bad data is rejected with a clear
// error BEFORE Firebase coughs up its opaque INVALID_DATA.
//
// Why these live here and not in crmTypes.ts:
//   - crmTypes.ts is shapes-only (consumed by UI, migration scripts, PDF
//     exporter — anything that just needs to know the field names). No
//     runtime behavior allowed.
//   - This file is runtime guards. crm.ts pulls these in; UI components
//     can pull them in too if they want to pre-validate before calling
//     the SDK (nicer error messages in forms).

import type {
  QuoteStatus,
  OrderStatus,
} from './crmTypes';

// =====================================================
// stripUndefined — recursive
// =====================================================

/**
 * Recursively strip undefined-valued keys from plain objects. Preserves Date
 * and other class instances as-is (they have non-Object prototypes — Firebase
 * RTDB's serializer handles Dates natively, but we don't want to mangle them
 * by walking their internal slots).
 *
 * Arrays: we DO NOT silently drop undefined slots. Firebase rejects undefined
 * inside arrays too, and silently re-shaping an array would mask UI bugs
 * (a missing per-tier price would shift indexes one column to the left,
 * which is way worse than a thrown error pointing at the bad index).
 *
 * The fieldPath parameter is for error messages — pass the entity name from
 * the SDK call site ("createPart input", "Quote.outcome", etc).
 */
export function stripUndefinedDeep<T>(value: T, fieldPath = 'input'): T {
  return walkStrip(value, fieldPath) as T;
}

function walkStrip(value: unknown, fieldPath: string): unknown {
  if (value === null) return null;
  if (value === undefined) {
    // Top-level undefined at the entry shouldn't happen (callers pass an
    // object) but defensive: just bubble it up so the object-walk drops it.
    return undefined;
  }

  if (Array.isArray(value)) {
    // Walk every slot; reject undefined entries with a clear error naming
    // the index. Firebase would reject these anyway with a less-useful message.
    const out: unknown[] = new Array(value.length);
    for (let i = 0; i < value.length; i++) {
      const v = value[i];
      if (v === undefined) {
        throw new Error(
          `${fieldPath}[${i}] is undefined — Firebase RTDB rejects undefined array entries. ` +
            `Use null if you mean "explicitly absent", or omit the entry entirely.`,
        );
      }
      out[i] = walkStrip(v, `${fieldPath}[${i}]`);
    }
    return out;
  }

  // Preserve class instances (Date, Map, Set, etc) — anything not a plain
  // object. Object.getPrototypeOf(value) === Object.prototype is the cheap
  // "is plain object" check; null-prototype objects (Object.create(null))
  // are also considered plain.
  if (typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      // Class instance — pass through untouched.
      return value;
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = walkStrip(v, `${fieldPath}.${k}`);
    }
    return out;
  }

  return value;
}

// =====================================================
// Finite-number guard
// =====================================================

/**
 * Walk an object and throw if any numeric leaf is NaN, Infinity, or
 * -Infinity. Firebase rejects all three at the network layer with a confusing
 * INVALID_DATA error — we throw earlier with the exact field path so the UI
 * can highlight the bad input.
 *
 * Called from createX/updateX AFTER stripUndefined (no point checking keys
 * we're about to drop). The fieldPath param is the same pattern as
 * stripUndefinedDeep — name the entity at the call site.
 */
export function assertFiniteNumbers(value: unknown, fieldPath = 'input'): void {
  walkFinite(value, fieldPath);
}

function walkFinite(value: unknown, fieldPath: string): void {
  if (value === null || value === undefined) return;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      const kind = Number.isNaN(value) ? 'NaN' : value > 0 ? 'Infinity' : '-Infinity';
      throw new Error(
        `${fieldPath} is ${kind} — Firebase RTDB only accepts finite numbers. ` +
          `Use null or omit the field entirely if the value is unknown.`,
      );
    }
    return;
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) walkFinite(value[i], `${fieldPath}[${i}]`);
    return;
  }
  if (typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      // Class instance — skip walk (Date etc don't carry user numeric leaves
      // for our purposes).
      return;
    }
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      walkFinite(v, `${fieldPath}.${k}`);
    }
  }
}

// =====================================================
// ISO-date assertion
// =====================================================

/**
 * Throws if the value isn't a parseable ISO 8601 string. Allows undefined
 * (means "not set") and empty string (which the UI uses for cleared
 * date pickers). Anything else gets validated through Date.parse.
 *
 * Why permissive on empty string: Justin's UI binds date inputs to fields
 * that are sometimes blanked out. Rejecting "" would force every form
 * component to special-case the blank state. Firebase happily stores "" so
 * this matches what already works.
 */
export function assertIsoDate(
  value: unknown,
  fieldName: string,
): asserts value is string | undefined {
  if (value === undefined) return;
  if (value === '') return;
  if (typeof value !== 'string') {
    throw new Error(`${fieldName} must be an ISO 8601 string, got ${typeof value}`);
  }
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) {
    throw new Error(
      `${fieldName} is not a parseable ISO 8601 date: ${JSON.stringify(value)}`,
    );
  }
}

// =====================================================
// Allowed status transitions
// =====================================================

/**
 * QuoteStatus state machine. Read as: from → allowed-to[].
 *
 * Intent (per spec/14 + Justin's day-to-day flow):
 *   - lead is the bucket of "we got an RFQ but haven't started"
 *   - draft → sent is the work-then-ship transition
 *   - sent fans out: follow-up nudges (follow_up), back-and-forth
 *     (negotiating), or terminal (won/lost/expired)
 *   - won and lost are terminal (only allow self for idempotency —
 *     the UI may double-click)
 *   - expired can be re-quoted (back to sent) or marked lost
 *   - imported is historical bootstrap — only allow correcting to won/lost
 */
export const QUOTE_STATUS_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  lead: ['lead', 'draft', 'lost'],
  draft: ['draft', 'sent', 'lost'],
  sent: ['sent', 'follow_up', 'negotiating', 'won', 'lost', 'expired'],
  follow_up: ['follow_up', 'negotiating', 'won', 'lost', 'expired'],
  negotiating: ['negotiating', 'won', 'lost', 'expired', 'sent'],
  won: ['won'], // terminal
  lost: ['lost'], // terminal
  expired: ['expired', 'sent', 'lost'], // re-quote allowed
  imported: ['imported', 'won', 'lost'], // historical correction
};

/**
 * OrderStatus state machine.
 *
 * Derived from the current OrderStatus enum: 'draft' | 'in_production' |
 * 'shipped' | 'closed' | 'void'. Standard happy path is draft →
 * in_production → shipped → closed. void is the "cancelled / killed"
 * terminal; allowed from any non-terminal state.
 *
 * TODO confirm with Justin: do we ever back up (shipped → in_production
 * for re-work)? For now allowing shipped → in_production so re-work doesn't
 * require a workaround. Same for closed → shipped (un-close after an
 * accidental close).
 */
export const ORDER_STATUS_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  draft: ['draft', 'in_production', 'void'],
  in_production: ['in_production', 'shipped', 'void'],
  shipped: ['shipped', 'in_production', 'closed', 'void'], // back-up to in_production for re-work
  closed: ['closed', 'shipped'], // un-close in case of accidental close
  void: ['void'], // terminal
};

/**
 * Throws a clear error if the requested status transition isn't in the
 * allowed table for this entity.
 */
export function assertQuoteTransition(
  quoteId: string,
  from: QuoteStatus,
  to: QuoteStatus,
): void {
  const allowed = QUOTE_STATUS_TRANSITIONS[from];
  if (!allowed) {
    throw new Error(
      `Quote ${quoteId} has unknown status "${from}" — cannot transition to "${to}".`,
    );
  }
  if (!allowed.includes(to)) {
    throw new Error(
      `Cannot transition quote ${quoteId} from "${from}" to "${to}". ` +
        `Allowed transitions from "${from}": ${allowed.join(', ')}.`,
    );
  }
}

export function assertOrderTransition(
  orderId: string,
  from: OrderStatus,
  to: OrderStatus,
): void {
  const allowed = ORDER_STATUS_TRANSITIONS[from];
  if (!allowed) {
    throw new Error(
      `Order ${orderId} has unknown status "${from}" — cannot transition to "${to}".`,
    );
  }
  if (!allowed.includes(to)) {
    throw new Error(
      `Cannot transition order ${orderId} from "${from}" to "${to}". ` +
        `Allowed transitions from "${from}": ${allowed.join(', ')}.`,
    );
  }
}

// =====================================================
// partIds ↔ partQuotedSpecs cross-check
// =====================================================

/**
 * Quote.partIds[] is the authoritative ordered list of parts; partQuotedSpecs
 * is a keyed dict of per-part snapshots. Their keys MUST match exactly —
 * a partId without a spec is a phantom (PDF will skip it silently or NPE);
 * a spec without a partId is an orphan (pricing math will sum it but the
 * PDF won't render it). Both are bugs.
 *
 * We don't auto-prune because that would mask the UI bug that produced the
 * mismatch. Throw with the specific orphan/phantom lists so the caller can
 * see exactly what went wrong.
 */
export function assertPartIdsSpecsConsistent(
  partIds: string[] | undefined,
  partQuotedSpecs: Record<string, unknown> | undefined,
  contextLabel = 'quote',
): void {
  const ids = partIds ?? [];
  const specKeys = Object.keys(partQuotedSpecs ?? {});
  const idSet = new Set(ids);
  const specSet = new Set(specKeys);

  const phantoms = ids.filter((id) => !specSet.has(id));
  const orphans = specKeys.filter((k) => !idSet.has(k));

  if (phantoms.length === 0 && orphans.length === 0) return;

  const parts: string[] = [];
  if (phantoms.length > 0) {
    parts.push(
      `partIds without a matching partQuotedSpecs entry: [${phantoms.join(', ')}]`,
    );
  }
  if (orphans.length > 0) {
    parts.push(
      `partQuotedSpecs keys without a matching partIds entry: [${orphans.join(', ')}]`,
    );
  }
  throw new Error(`${contextLabel}: partIds / partQuotedSpecs mismatch. ${parts.join('; ')}.`);
}
